import { IconName, useCallbackRef, useDisclosureWithContext, useUserContext, type CareOrder } from '@talos/kyoko';
import type { MenuItemDef } from 'ag-grid-community';
import { useMemo, type ReactNode } from 'react';
import {
  CareOrderNettingPreviewDialog,
  CareOrderNettingPreviewMode,
  type CareOrderNettingPreviewDialogContext,
} from '../CareOrderNettingPreview/CareOrderNettingPreviewDialog';

interface GetModifyNettingMenuItemParams<TCareOrder extends CareOrder> {
  contextCareOrder?: TCareOrder;
  selectedCareOrders: TCareOrder[];
}

export const useModifyNettingMenuItem = <TCareOrder extends CareOrder>(): {
  getMenuItem: (params: GetModifyNettingMenuItemParams<TCareOrder>) => MenuItemDef | null;
  dialogs: ReactNode;
} => {
  const nettingPreviewDisclosure = useDisclosureWithContext<CareOrderNettingPreviewDialogContext>();
  const { user } = useUserContext();

  const getMenuItem = useCallbackRef(
    ({ contextCareOrder, selectedCareOrders }: GetModifyNettingMenuItemParams<TCareOrder>) => {
      if (contextCareOrder?.isNetted !== true || contextCareOrder.User !== user.Name) {
        return null;
      }

      return {
        name: 'Modify Netting',
        action: () => {
          nettingPreviewDisclosure.open({
            mode: CareOrderNettingPreviewMode.Modify,
            selectedCareOrders: selectedCareOrders.length > 0 ? selectedCareOrders : [contextCareOrder],
            nettedCareOrder: contextCareOrder,
          });
        },
        icon: `<i class="ag-icon ${IconName.ArrowsReduce}"/>`,
      } satisfies MenuItemDef;
    }
  );

  const dialogs = useMemo(
    () => <CareOrderNettingPreviewDialog disclosure={nettingPreviewDisclosure} />,
    [nettingPreviewDisclosure]
  );

  return {
    getMenuItem,
    dialogs,
  };
};
